import { createSlice, Dispatch } from '@reduxjs/toolkit';
import {
  AdditionalParametersForm,
  DatesAndFormatForm,
  GlobalExportState,
} from '../../@types/globalExport';
import {
  InterkommunalerLastenausgleichConfig,
  TourFetchingType,
  TourTender,
} from '../../@types/tour';
import { apiSlice } from './apiSlice';
import { ExportGroup } from '../../@types/configuration';

const initialState: GlobalExportState = {
  isLoading: false,
  error: null,
  tourIds: [],
  schoolIds: [],
  tourBundleIds: [],
  transportationCompanyIds: [],
  employeeIds: [],
  vehicleIds: [],
  from: '',
  until: '',
  format: '',
  tourFetchingType: TourFetchingType.ALL,
  useEmptyKmCost: false,
  showCosts: true,
  respectAbsence: true,
  respectWaitingTime: true,
  overrideOccupiedDistance: true,
  costcenter: '',
  groupByCostcenters: false,
  showSegmentKilometer: false,
  invoiceWithZeroVat: false,
  wheelchairTransportWithZeroVat: false,
  wheelchairStudentsProportionallyWithZeroVat: false,
};

const slice = createSlice({
  name: 'globalexport',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // FIXME Frank: one or two functions?
    updateTourAndSchoolAndTransportIds(state, action) {
      const ids = action.payload;
      state.tourIds = ids.tourIds;
      state.schoolIds = ids.schoolIds;
      state.tourBundleIds = ids.tourBundleIds;
      state.transportationCompanyIds = ids.transportationCompanyIds;
      state.employeeIds = ids.employeeIds;
      state.vehicleIds = ids.vehicleIds;
      state.tourBundleIds = ids.tourBundleIds;
    },

    updateDatesAndFormat(state, action) {
      const datesAndFormat = action.payload;
      state.from = datesAndFormat.from;
      state.until = datesAndFormat.until;
      state.format = datesAndFormat.format;
      state.tourFetchingType = datesAndFormat.tourFetchingType;
    },

    updateAdditionalParameters(state, action) {
      const additionalParameters = action.payload;
      state.useEmptyKmCost = additionalParameters.useEmptyKmCost;
      state.showCosts = additionalParameters.showCosts;
      state.respectAbsence = additionalParameters.respectAbsence;
      state.respectWaitingTime = additionalParameters.respectWaitingTime;
      state.overrideOccupiedDistance = additionalParameters.overrideOccupiedDistance;
      state.costcenter = additionalParameters.costcenter;
      state.groupByCostcenters = additionalParameters.groupByCostcenters;
      state.showSegmentKilometer = additionalParameters.showSegmentKilometer;
      state.invoiceWithZeroVat = additionalParameters.invoiceWithZeroVat ?? false;
      state.wheelchairTransportWithZeroVat =
        additionalParameters.wheelchairTransportWithZeroVat ?? false;
      state.wheelchairStudentsProportionallyWithZeroVat =
        additionalParameters.wheelchairStudentsProportionallyWithZeroVat ?? false;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function updateIds(data: {
  tourIds: string[];
  schoolIds: string[];
  tourBundleIds: string[];
  transportationCompanyIds: string[];
  employeeIds: string[];
  vehicleIds: string[];
}) {
  return (dispatch: Dispatch) => {
    dispatch(slice.actions.updateTourAndSchoolAndTransportIds(data));
  };
}

export function updateDateAndFormat(data: DatesAndFormatForm) {
  return (dispatch: Dispatch) => {
    console.debug(`#### updateDateAndFormat data ${JSON.stringify(data)}`);
    const convertedFrom = data.from instanceof Date ? data.from.getTime() : data.from;
    const convertedUntil = data.until instanceof Date ? data.until.getTime() : data.until;
    dispatch(
      slice.actions.updateDatesAndFormat({
        from: convertedFrom,
        until: convertedUntil,
        format: data.format,
        tourFetchingType: data.tourFetchingType,
      })
    );
  };
}

export function updateAdditionalParameters(data: AdditionalParametersForm) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateAdditionalParameters({
        useEmptyKmCost: data.useEmptyKmCost,
        showCosts: data.showCosts,
        respectAbsence: data.respectAbsence,
        respectWaitingTime: data.respectWaitingTime,
        overrideOccupiedDistance: data.overrideOccupiedDistance,
        costcenter: data.costcenter,
        groupByCostcenters: data.groupByCostcenters,
        showSegmentKilometer: data.showSegmentKilometer,
        invoiceWithZeroVat: data.invoiceWithZeroVat ?? false,
        wheelchairTransportWithZeroVat: data.wheelchairTransportWithZeroVat ?? false,
        wheelchairStudentsProportionallyWithZeroVat:
          data.wheelchairStudentsProportionallyWithZeroVat ?? false,
      })
    );
  };
}

export const tourTenderApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    updateTourTender: builder.mutation({
      query: ({ tourTender }) => ({
        url: '/export/global/tour-tender',
        method: 'PUT',
        body: tourTender,
      }),
      invalidatesTags: ['TourTender'],
    }),
    getTourTenderConfig: builder.query<TourTender, undefined>({
      query: () => '/export/global/tour-tender',
      providesTags: ['TourTender'],
    }),
  }),
});

export const interkommunalerLastenausgleichApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    exportInterkommunalerLastenausgleichJob: builder.mutation({
      query: ({ interkommunalerLastenausgleichData, transportationPeriodId }) => ({
        url: `/export/global/interkommunaler-lastenausgleich${transportationPeriodId !== undefined ? `?transportationPeriodId=${transportationPeriodId}` : ''}`,
        method: 'PUT',
        body: interkommunalerLastenausgleichData,
      }),
      invalidatesTags: ['InterkommunalerLastenausgleichConfig'],
    }),
    getInterkommunalerLastenausgleichConfig: builder.query<
      InterkommunalerLastenausgleichConfig,
      undefined
    >({
      query: () => '/export/global/interkommunaler-lastenausgleich',
      providesTags: ['InterkommunalerLastenausgleichConfig'],
    }),
  }),
});

export const exportGroupApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getExportGroups: builder.query<ExportGroup[], void>({
      query: () => '/export/global/export-groups',
      providesTags: ['ExportGroups'],
    }),
  }),
});

export const { useUpdateTourTenderMutation, useGetTourTenderConfigQuery } = tourTenderApiSlice;
export const {
  useGetInterkommunalerLastenausgleichConfigQuery,
  useExportInterkommunalerLastenausgleichJobMutation,
} = interkommunalerLastenausgleichApiSlice;
export const { useGetExportGroupsQuery } = exportGroupApiSlice;
